import type {
  CreateQuoteRequestRequest,
  GetMyQuoteRequestsParams,
  GetPartnersParams,
  GetRecommendedPartnersParams,
  PartnersDataSource,
} from '~/api/data-source/partners';
import { partnersDataSource } from '~/api/data-source/partners';
import type {
  AcceptQuoteResponse,
  CreateQuoteResponse,
  MyQuoteRequestList,
  PartnerDetail,
  PartnerList,
  QuoteRequestDetail,
  QuoteRequestSummary,
  RecommendedPartnerList,
} from '~/api/model';
import {
  acceptQuoteResponseSchema,
  createQuoteResponseSchema,
  myQuoteRequestListSchema,
  parseModel,
  partnerDetailSchema,
  partnerListSchema,
  quoteRequestDetailSchema,
  quoteRequestSummarySchema,
  recommendedPartnerListSchema,
} from '~/api/model';

// Re-export types for convenience
// Request 타입 및 Params는 data-source에서
export type {
  CreateQuoteRequestRequest,
  GetMyQuoteRequestsParams,
  GetPartnersParams,
  GetRecommendedPartnersParams,
} from '~/api/data-source/partners';

// Response 타입은 Model에서
export type {
  AcceptQuoteResponse,
  CreateQuoteResponse,
  MyQuoteRequestItem,
  MyQuoteRequestList,
  PartnerCardItem,
  PartnerDetail,
  PartnerGrade,
  PartnerList,
  QuoteRequestDetail,
  QuoteRequestSummary,
  QuoteResponseSummaryItem,
  ReceivedQuoteItem,
  RecommendedPartnerList,
} from '~/api/model';

export interface PartnersRepository {
  /** 파트너 목록 조회 */
  getPartners(params: GetPartnersParams): Promise<PartnerList>;

  /** 추천 파트너 목록 조회 (지역 기반) */
  getRecommendedPartners(
    params: GetRecommendedPartnersParams,
  ): Promise<RecommendedPartnerList>;

  /** 파트너 상세 조회 */
  getPartnerDetail(partnerId: number): Promise<PartnerDetail>;

  /** 내 견적 요청 목록 조회 */
  getMyQuoteRequests(
    params: GetMyQuoteRequestsParams,
  ): Promise<MyQuoteRequestList>;

  /** 견적 요청 상세 조회 */
  getQuoteRequestDetail(quoteRequestId: number): Promise<QuoteRequestDetail>;

  /** 견적 요청 생성 */
  createQuoteRequest(
    request: CreateQuoteRequestRequest,
  ): Promise<CreateQuoteResponse>;

  /** 견적 수락 */
  acceptQuote(quoteResponseId: number): Promise<AcceptQuoteResponse>;

  /** 견적 거절 */
  rejectQuote(quoteResponseId: number): Promise<void>;

  /** 견적 요청 요약 조회 */
  getQuoteRequestSummary(): Promise<QuoteRequestSummary>;
}

export class PartnersRepositoryImpl implements PartnersRepository {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    private readonly dataSource: PartnersDataSource = partnersDataSource,
  ) {}

  async getPartners(params: GetPartnersParams) {
    const response = await this.dataSource.getPartners(params);
    return parseModel(partnerListSchema, response, 'PartnerList');
  }

  async getRecommendedPartners(params: GetRecommendedPartnersParams) {
    const response = await this.dataSource.getRecommendedPartners(params);
    return parseModel(
      recommendedPartnerListSchema,
      response,
      'RecommendedPartnerList',
    );
  }

  async getPartnerDetail(partnerId: number) {
    const response = await this.dataSource.getPartnerDetail(partnerId);
    return parseModel(partnerDetailSchema, response, 'PartnerDetail');
  }

  async getMyQuoteRequests(params: GetMyQuoteRequestsParams) {
    const response = await this.dataSource.getMyQuoteRequests(params);
    return parseModel(myQuoteRequestListSchema, response, 'MyQuoteRequestList');
  }

  async getQuoteRequestDetail(quoteRequestId: number) {
    const response =
      await this.dataSource.getQuoteRequestDetail(quoteRequestId);
    const result = parseModel(
      quoteRequestDetailSchema,
      response,
      'QuoteRequestDetail',
    );
    return {
      ...result,
      sentResponses: result.sentResponses.filter(
        (item) => item.membershipActive,
      ),
    };
  }

  async createQuoteRequest(request: CreateQuoteRequestRequest) {
    const response = await this.dataSource.createQuoteRequest(request);
    return parseModel(
      createQuoteResponseSchema,
      response,
      'CreateQuoteResponse',
    );
  }

  async acceptQuote(quoteResponseId: number) {
    const response = await this.dataSource.acceptQuote(quoteResponseId);
    return parseModel(
      acceptQuoteResponseSchema,
      response,
      'AcceptQuoteResponse',
    );
  }

  async rejectQuote(quoteResponseId: number) {
    return this.dataSource.rejectQuote(quoteResponseId);
  }

  async getQuoteRequestSummary() {
    const response = await this.dataSource.getQuoteRequestSummary();
    return parseModel(
      quoteRequestSummarySchema,
      response,
      'QuoteRequestSummary',
    );
  }
}

export const partnersRepository = new PartnersRepositoryImpl();
